import { useMemo, useState } from "react";

import { manageableProgrammes as manageableProgrammeList } from "@/components/teams/manageable-programmes";
import { ModuleOwnership } from "@/components/teams/ModuleOwnership";
import {
  type PermissionOption,
  ProgrammePermissions,
} from "@/components/teams/ProgrammePermissions";
import {
  builderLoads,
  builderPickerRefusal,
  compareText,
  groupResponsibilities,
  moduleOwnerRows,
  modulePickerRefusal,
  type ResponsibilityGroup,
  revokeRefusal,
  teamRefusal,
  userOptions,
} from "@/components/teams/team-assignments";
import {
  DEFAULT_ALLOWED_RESPONSIBILITIES,
  permissionCell,
  RESPONSIBILITY_OPTIONS,
  writeRefusals,
} from "@/components/teams/team-permissions";
import { AddStaffDialog, AssignBuilderDialog } from "@/components/teams/TeamDialogs";
import { TeamReadState } from "@/components/teams/TeamReadState";
import { TeamStatus } from "@/components/teams/TeamStatus";
import { useLeadTeamManagement } from "@/components/teams/useLeadTeamManagement";
import { useTeamAssignments } from "@/components/teams/useTeamAssignments";
import { Alert, Button, TabPanel, Tabs } from "@/components/ui";
import type {
  ProgrammePlatformClient,
  ProgrammeSummary,
  SessionContext,
  UserSummary,
} from "@/platform";
import type { ProgrammeResponsibility } from "@/server/types";
import { evaluateDevelopmentSetup } from "@/utils/development-setup";

interface AssignmentManagementProps {
  client: ProgrammePlatformClient;
  session: SessionContext;
  programmes: ProgrammeSummary[];
  users: UserSummary[];
  /** The programme whose team this shows; the caller owns the choice. */
  fixedProgrammeId: string;
  allowedResponsibilities?: readonly ProgrammeResponsibility[];
  onChanged?: () => Promise<void> | void;
  /** The tenant setting, where the caller has already read it. */
  allowLeadTeamManagement?: boolean;
  /** Shows the team to an account that may not change it; every write refuses. */
  readOnly?: boolean;
  /** Head the team with the programme's name. Off where a header already names it. */
  named?: boolean;
}

export function AssignmentManagement({
  client,
  session,
  programmes,
  users,
  fixedProgrammeId: programmeId,
  allowedResponsibilities = DEFAULT_ALLOWED_RESPONSIBILITIES,
  onChanged = undefined,
  allowLeadTeamManagement: knownLeadTeamManagement = undefined,
  readOnly = false,
  named = true,
}: AssignmentManagementProps) {
  const isTenantAdmin =
    session.subject.accountRole === "client-admin" ||
    session.subject.accountRole === "platform-admin";
  const allowLeadTeamManagement = useLeadTeamManagement(client, knownLeadTeamManagement);

  // The route's own list, not a second copy of the rule: the two had grown
  // different comparators, so the picker and the list beside it disagreed
  // about where a numbered title belongs.
  const manageableProgrammes = useMemo(
    () => manageableProgrammeList(session, programmes, allowLeadTeamManagement),
    [allowLeadTeamManagement, programmes, session],
  );
  const activeUsers = useMemo(
    () =>
      users
        .filter((user) => user.status === "active")
        .sort((left, right) => compareText(left.displayName, right.displayName)),
    [users],
  );
  const moduleOwnerCandidates = activeUsers;
  const responsibilityCandidates = activeUsers;
  const responsibilityOptions = useMemo(
    () =>
      RESPONSIBILITY_OPTIONS.filter((option) =>
        allowedResponsibilities.includes(option.value),
      ).sort((left, right) => compareText(left.label, right.label)),
    [allowedResponsibilities],
  );
  // Viewer where it is allowed: the lowest permission that still puts someone
  // on the team, so adding a person grants nothing they have not been given.
  const initialResponsibility = allowedResponsibilities.includes("viewer")
    ? ("viewer" as const)
    : responsibilityOptions[0]?.value;
  const [pickedUserId, setSelectedUserId] = useState("");
  const [pickedModuleId, setSelectedModuleId] = useState("");
  const [pickedModuleOwnerId, setSelectedModuleOwnerId] = useState("");

  const {
    programme,
    responsibilities,
    activeModuleOwners,
    loading,
    loadError,
    retry,
    feedback,
    clearFeedback,
    addStaffMember,
    toggleResponsibility,
    assignModuleOwner,
    revokeModuleOwner,
  } = useTeamAssignments({ client, programmeId, onChanged });

  const [tab, setTab] = useState<"people" | "modules">("people");
  const [addingStaff, setAddingStaff] = useState(false);
  const [assigningBuilder, setAssigningBuilder] = useState(false);

  // Adjusted during render, which is React's own answer for state that resets
  // when an input changes. The combobox used to do this itself, and the
  // master-detail route renders none: it changes the programme from outside,
  // so a banner stayed up over the next team and its picks stayed armed.
  const [lastProgrammeId, setLastProgrammeId] = useState(programmeId);
  if (programmeId !== lastProgrammeId) {
    setLastProgrammeId(programmeId);
    setSelectedUserId("");
    setSelectedModuleId("");
    setSelectedModuleOwnerId("");
    setTab("people");
    setAddingStaff(false);
    setAssigningBuilder(false);
    clearFeedback();
  }

  // Everyone the add dialog can still offer: an active user who does not
  // already hold a responsibility here.
  const addableUsers = useMemo(
    () =>
      responsibilityCandidates.filter(
        (user) => !responsibilities.some((one) => one.userId === user.id),
      ),
    [responsibilityCandidates, responsibilities],
  );
  // Validated against the list the dialog renders, not the wider active-user
  // one: a pick the dialog has dropped otherwise leaves an empty field over
  // an armed submit, which writes a second responsibility for one person.
  const selectedUserId = addableUsers.some((user) => user.id === pickedUserId) ? pickedUserId : "";
  const selectedModuleOwnerId = moduleOwnerCandidates.some(
    (user) => user.id === pickedModuleOwnerId,
  )
    ? pickedModuleOwnerId
    : "";
  const selectedModuleId = (programme?.structured.modules ?? []).some(
    (module) => module.id === pickedModuleId,
  )
    ? pickedModuleId
    : "";

  const modules = [...(programme?.structured.modules ?? [])].sort((left, right) =>
    compareText(left.title, right.title),
  );
  const ownersByModuleId = new Map(activeModuleOwners.map((owner) => [owner.moduleId, owner]));
  const leadAssignments = responsibilities.filter(
    (assignment) => assignment.responsibility === "lead",
  );
  const selectedProgramme = manageableProgrammes.find((item) => item.id === programmeId);
  const isLocked =
    programme?.visibilityState === "locked" || programme?.workflowState === "Exported and Locked";
  const developmentSetup = programme ? evaluateDevelopmentSetup(programme.structured) : null;
  const builderAssignmentBlocked = programme?.workflowState === "Draft";
  const assignedModuleCount = modules.filter((module) => ownersByModuleId.has(module.id)).length;
  const workload = builderLoads(activeModuleOwners, responsibilities, users);

  const refusal = teamRefusal(session, allowLeadTeamManagement, readOnly);
  const { addStaffRefusal, assignBuilderRefusal } = writeRefusals({
    locked: isLocked,
    saving: feedback.status === "working",
    permitted: refusal === null,
    staffReady: Boolean(selectedUserId) && Boolean(initialResponsibility),
    builderPermitted: !builderAssignmentBlocked,
    builderReady: Boolean(selectedModuleId) && Boolean(selectedModuleOwnerId),
  });
  const groupedResponsibilities = groupResponsibilities(responsibilities, users);

  const permissionCellFor = (group: ResponsibilityGroup, option: PermissionOption) =>
    permissionCell(group, option, {
      responsibilities,
      allowedResponsibilities,
      refusal,
      locked: isLocked,
      saving: feedback.status === "working",
    });
  if (!manageableProgrammes.length && !readOnly) {
    return <Alert tone="info">No programmes are available for Programme teams.</Alert>;
  }

  // The record this screen loaded, ahead of the summary list, which is
  // refreshed independently and goes stale on a rename made elsewhere.
  const programmeTitle = programme?.structured.title ?? selectedProgramme?.title;

  return (
    <div className="space-y-6" data-testid="assignment-management">
      <TeamStatus
        title={named ? (programmeTitle ?? "Programme team") : undefined}
        workflowState={programme?.workflowState ?? selectedProgramme?.workflowState}
        saveRevision={programme?.saveRevision ?? selectedProgramme?.saveRevision}
        leadCount={leadAssignments.length}
        assignedModuleCount={assignedModuleCount}
        moduleCount={modules.length}
        loadError={loadError}
        loading={loading}
        feedback={feedback}
        locked={isLocked}
        leadManagementDisabled={!isTenantAdmin && !allowLeadTeamManagement}
        builderAssignmentBlocked={builderAssignmentBlocked}
        developmentSetup={developmentSetup}
      />
      {/* A failed read settles with empty lists, so every panel below would
          state what it found: no responsibilities, no modules, no lead. */}
      {loading || loadError ? (
        <TeamReadState loading={loading} onRetry={retry} />
      ) : (
        // One surface at a time. Both panels stacked put a roster, two forms,
        // a workload and a module table on one screen, which is more than the
        // reader is ever doing at once.
        <>
          {readOnly ? (
            <Alert tone="info" data-testid="team-read-only">
              {refusal}
            </Alert>
          ) : null}
          <Tabs
            variant="lift"
            // The panel below is the card these tabs hang off: no gap from `space-y-6`.
            className="mb-0!"
            items={[
              { key: "people", label: "People" },
              { key: "modules", label: "Modules" },
            ]}
            value={tab}
            onChange={(key: string) => {
              setTab(key === "modules" ? "modules" : "people");
            }}
            aria-label="Team surfaces"
            idPrefix="team"
            data-testid="assignment-management-tabs"
          />

          <TabPanel idPrefix="team" tabKey="people" active={tab === "people"}>
            <ProgrammePermissions
              groups={groupedResponsibilities}
              options={responsibilityOptions}
              cellFor={permissionCellFor}
              onToggle={(group, option) => toggleResponsibility(group, option.value)}
              addStaff={
                readOnly ? null : (
                  <Button
                    variant="primary"
                    size="sm"
                    icon="user-plus"
                    onClick={() => {
                      setAddingStaff(true);
                    }}
                    data-testid="assignment-management-open-add-staff"
                  >
                    Add staff
                  </Button>
                )
              }
            />
          </TabPanel>

          <TabPanel idPrefix="team" tabKey="modules" active={tab === "modules"}>
            <ModuleOwnership
              rows={moduleOwnerRows(modules, activeModuleOwners, users)}
              workload={workload}
              assignedCount={assignedModuleCount}
              onRevoke={revokeModuleOwner}
              revokeDisabled={readOnly || isLocked || feedback.status === "working"}
              revokeReason={revokeRefusal(readOnly ? refusal : null, isLocked)}
              picker={
                readOnly ? null : (
                  <Button
                    variant="primary"
                    size="sm"
                    icon="user-switch"
                    onClick={() => {
                      setAssigningBuilder(true);
                    }}
                    data-testid="assignment-management-open-assign-builder"
                  >
                    Assign builder
                  </Button>
                )
              }
            />
          </TabPanel>

          {addingStaff && (
            <AddStaffDialog
              open={addingStaff}
              onClose={() => {
                setAddingStaff(false);
              }}
              programmeTitle={programmeTitle ?? ""}
              value={selectedUserId}
              onChange={setSelectedUserId}
              options={userOptions(addableUsers)}
              permissionOptions={responsibilityOptions}
              initialPermission={initialResponsibility}
              onAdd={(permission) => {
                addStaffMember(selectedUserId, permission);
                // No explicit clear: the pick drops out of `addableUsers`
                // the moment the write lands, so it clears itself — and a
                // write that fails keeps the pick the reader made.
                setAddingStaff(false);
              }}
              addDisabled={addStaffRefusal !== null}
              disabledReason={addStaffRefusal ?? undefined}
            />
          )}

          {assigningBuilder && (
            <AssignBuilderDialog
              open={assigningBuilder}
              onClose={() => {
                setAssigningBuilder(false);
              }}
              programmeTitle={programmeTitle ?? ""}
              moduleId={selectedModuleId}
              onModuleChange={setSelectedModuleId}
              modules={modules.map((module) => ({
                value: module.id,
                label: module.title,
                hint: module.code,
              }))}
              moduleRefusal={modulePickerRefusal(builderAssignmentBlocked, modules.length)}
              ownerId={selectedModuleOwnerId}
              onOwnerChange={setSelectedModuleOwnerId}
              builders={userOptions(moduleOwnerCandidates)}
              ownerRefusal={builderPickerRefusal(
                builderAssignmentBlocked,
                selectedModuleId,
                moduleOwnerCandidates.length,
              )}
              onAssign={() => {
                assignModuleOwner(selectedModuleId, selectedModuleOwnerId);
                setSelectedModuleId("");
                setSelectedModuleOwnerId("");
                setAssigningBuilder(false);
              }}
              assignDisabled={assignBuilderRefusal !== null}
              disabledReason={assignBuilderRefusal ?? undefined}
            />
          )}
        </>
      )}
    </div>
  );
}
